import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import '../styles/landing.css';

const Landing = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);

    // Redirect if already logged in
    useEffect(() => {
        if (user) {
            if (user.is_admin) {
                navigate('/admin');
            } else if (user.lifecycle_stage === 'preboarding' && user.preboarding_token) {
                // Redirect to preboarding dashboard
                navigate(`/preboard/${user.preboarding_token}/dashboard`);
            } else {
                navigate('/employee');
            }
        }
    }, [user, navigate]);

    useEffect(() => {
        const nav = document.querySelector('.landing-nav');
        const handleScroll = () => {
            if (window.scrollY > 50) {
                nav?.classList.add('scrolled');
            } else {
                nav?.classList.remove('scrolled');
            }
        };
        window.addEventListener('scroll', handleScroll);

        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('in-view');
                    obs.unobserve(entry.target);
                }
            });
        }, observerOptions);

        document.querySelectorAll('.scroll-animate').forEach((element) => {
            observer.observe(element);
        });

        // Handle smooth scrolling for hash links
        const handleHashChange = () => {
            const hash = window.location.hash;
            if (hash) {
                setTimeout(() => {
                    const element = document.querySelector(hash);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            }
        };
        handleHashChange();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, []);

    const scrollToSection = (e, id) => {
        e.preventDefault();
        const element = document.querySelector(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            // update URL optionally
            window.history.pushState(null, '', id);
        }
    };

    return (
        <div className="landing-body" style={{ background: '#fff' }}>
            <Helmet>
                <title>Envion | Empower Your Workforce with Intelligent Onboarding</title>
                <meta name="description" content="Envion helps organisations replace manual onboarding with AI-powered, personalised journeys. Intelligent pathways and automated systems from preboarding to day one." />
                <link rel="canonical" href="https://envion.org.uk" />
                <meta property="og:title" content="Envion" />
                <meta property="og:description" content="Envion helps organisations replace manual onboarding with AI-powered, personalised journeys. Intelligent pathways and automated systems from preboarding to day one." />
                <meta property="og:image" content="Will be provided" />
                <meta property="og:url" content="https://envion.org.uk" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Envion" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Envion" />
                <meta name="twitter:description" content="Envion helps organisations replace manual onboarding with AI-powered, personalised journeys. Intelligent pathways and automated systems from preboarding to day one." />
                <meta name="twitter:image" content="Will be provided" />
            </Helmet>
            {/* Navigation */}
            <nav className={`landing-nav${menuOpen ? ' nav-open' : ''}`}>
                <div className="logo">
                    <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fa-solid fa-layer-group logo-icon" style={{ color: 'var(--lp-primary-orange)' }}></i>
                        <span style={{ color: 'var(--lp-primary-orange)' }}>envion</span>
                    </Link>
                </div>
                <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)}>
                    <i className={menuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'}></i>
                </button>
                <div className="nav-links">
                    <div className="nav-item-dropdown">
                        <Link to="/solutions" className="nav-link">
                            Solutions <i className="fa-solid fa-chevron-down" style={{ fontSize: '0.7em', marginLeft: '6px', opacity: 0.7 }}></i>
                        </Link>
                        <div className="dropdown-menu">
                            <Link to="/solutions#use-cases" className="dropdown-item">
                                <div className="dropdown-content">
                                    <div className="dropdown-header">
                                        <strong>Preboarding</strong>
                                        <i className="fa-solid fa-arrow-right dropdown-arrow"></i>
                                    </div>
                                    <span>Engagement starts early.</span>
                                </div>
                            </Link>
                            <Link to="/solutions#use-cases" className="dropdown-item">
                                <div className="dropdown-content">
                                    <div className="dropdown-header">
                                        <strong>Onboarding</strong>
                                        <i className="fa-solid fa-arrow-right dropdown-arrow"></i>
                                    </div>
                                    <span>Accelerate ramp-up time.</span>
                                </div>
                            </Link>
                        </div>
                    </div>
                    <Link to="/about" className="nav-link">About Us</Link>
                    <a href="#how-it-works" onClick={(e) => scrollToSection(e, '#how-it-works')} className="nav-link">How it Works</a>
                    <Link to="/contact" className="nav-link">Contact</Link>
                </div>
                <div className="auth-buttons">
                    <button onClick={() => navigate('/login')} className="btn-lp btn-lp-secondary w-full sm:w-auto text-center" style={{ cursor: 'pointer' }}>Log In</button>
                    <Link to="/contact" className="btn-lp btn-lp-primary">Get a Quote</Link>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero-section" style={{ position: 'relative', textAlign: 'center', flexDirection: 'column', alignItems: 'center' }}>
                <div className="hero-bg-glow"></div>
                <div className="hero-content" style={{ textAlign: 'center', maxWidth: '900px', zIndex: 2, position: 'relative' }}>
                    <div className="hero-badge">
                        <i className="fa-solid fa-star"></i>
                        <span>Enterprise Employee Onboarding Solutions</span>
                    </div>

                    <h1 className="hero-headline">
                        Outstanding onboarding outcomes with <span className="highlight-text">envion</span>
                    </h1>

                    <p className="hero-subtitle" style={{ maxWidth: '780px', margin: '0 auto 40px' }}>
                        Automate preboarding experiences and intelligent onboarding pathways that ensure every new
                        employee feels valued, prepared, and empowered to succeed.
                    </p>

                    <div className="cta-group" style={{ justifyContent: 'center', marginBottom: '60px' }}>
                        <a href="https://outlook.office.com/book/DiscoveryCall@envion.org.uk/?ismsaljsauthenabled"
                            className="btn-lp btn-lp-primary btn-lg" target="_blank" rel="noopener noreferrer">
                            Book a Meeting <i className="fa-solid fa-calendar-check"></i>
                        </a>
                        <a href="#platform" onClick={(e) => scrollToSection(e, '#platform')} className="btn-lp btn-lp-secondary btn-lg">
                            View Solutions <i className="fa-solid fa-arrow-down"></i>
                        </a>
                    </div>
                </div>
            </section>

            {/* Platform Section */}
            <section id="platform" className="solutions-section" style={{ position: 'relative' }}>
                <div className="platform-container" style={{ position: 'relative', zIndex: 2 }}>
                    <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '20px' }}>
                        Signed Offer to <span style={{ color: 'var(--lp-primary-orange)' }}>High Performer</span>
                    </h2>
                    <p className="section-subtitle"
                        style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 60px', fontSize: '1.25rem', color: 'var(--muted-foreground)' }}>
                        Whether you're hiring 1 or onboarding 100s, envion maximises your hiring ROI from pre-day 1 excitement to post-hire performance.
                    </p>

                    <div className="platform-layout platform-split-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '60px', alignItems: 'stretch' }}>
                        {/* Left Column: Stickers */}
                        <div className="platform-stickers" style={{ display: 'flex', flexDirection: 'column', gap: '30px', justifyContent: 'center' }}>
                            {/* Preboarding Sticker */}
                            <div className="solution-card scroll-animate animate-slide-up" style={{ textAlign: 'left', alignItems: 'flex-start', justifyContent: 'center', display: 'flex', flexDirection: 'column' }}>
                                <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '1.4rem', color: 'var(--lp-secondary-navy)' }}>
                                    Preboarding
                                </h3>
                                <p style={{ marginBottom: '24px', lineHeight: 1.6, color: 'rgba(15, 23, 42, 0.7)' }}>
                                    Transform waiting time into strategic employee engagement and preparation.
                                </p>
                                <Link to="/solutions#use-cases" className="btn-lp btn-lp-secondary btn-sm" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                                    Learn More <i className="fa-solid fa-arrow-right"></i>
                                </Link>
                            </div>

                            {/* Onboarding Sticker */}
                            <div className="solution-card scroll-animate animate-slide-up delay-100" style={{ textAlign: 'left', alignItems: 'flex-start', justifyContent: 'center', display: 'flex', flexDirection: 'column' }}>
                                <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '1.4rem', color: 'var(--lp-secondary-navy)' }}>
                                    Onboarding
                                </h3>
                                <p style={{ marginBottom: '24px', lineHeight: 1.6, color: 'rgba(15, 23, 42, 0.7)' }}>
                                    Deliver consistent, exceptional onboarding experiences that accelerate performance and build loyalty.
                                </p>
                                <Link to="/solutions#use-cases" className="btn-lp btn-lp-secondary btn-sm" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                                    Learn More <i className="fa-solid fa-arrow-right"></i>
                                </Link>
                            </div>
                        </div>

                        {/* Right Column: Dashboard Visual */}
                        <div className="platform-visuals" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <div className="dashboard-card scroll-animate animate-slide-up delay-200" style={{ maxWidth: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <div className="dashboard-header">
                                    <div className="header-icon"><i className="fa-solid fa-layer-group"></i></div>
                                    <h4>Active Cohorts</h4>
                                    <span className="status-badge">Live</span>
                                </div>

                                <div className="dashboard-grid dashboard-stats-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    {/* Preboarding Stat */}
                                    <div className="dashboard-stat-row">
                                        <div className="stat-info">
                                            <span className="stat-label">Preboarding</span>
                                            <span className="stat-value">12 Candidates</span>
                                        </div>
                                        <div className="stat-visual">
                                            <div className="avatar-stack">
                                                <span className="avatar"></span>
                                                <span className="avatar"></span>
                                                <span className="avatar"></span>
                                                <span className="avatar-more">+9</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Onboarding Stat */}
                                    <div className="dashboard-stat-row">
                                        <div className="stat-info">
                                            <span className="stat-label">Onboarding</span>
                                            <span className="stat-value">30 Employees</span>
                                        </div>
                                        <div className="stat-visual">
                                            <div className="progress-ring-mini" style={{ '--p': 75, '--c': 'var(--accent)' }}>
                                                <span className="progress-text">75%</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Risk Score */}
                                    <div className="dashboard-stat-row highlight-row">
                                        <div className="stat-info">
                                            <span className="stat-label">Avg Risk Score</span>
                                            <span className="stat-value" style={{ color: '#f59e0b' }}>Low (12%)</span>
                                        </div>
                                        <div className="stat-visual">
                                            <i className="fa-solid fa-shield-heart trend-icon" style={{ color: '#f59e0b', background: 'rgba(245, 158, 11, 0.1)' }}></i>
                                        </div>
                                    </div>

                                    {/* Compliance */}
                                    <div className="dashboard-stat-row">
                                        <div className="stat-info">
                                            <span className="stat-label">Compliance</span>
                                            <span className="stat-value">100% Ready</span>
                                        </div>
                                        <div className="stat-visual">
                                            <i className="fa-solid fa-check-circle trend-icon"></i>
                                        </div>
                                    </div>
                                </div>

                                {/* Task Completion Bar */}
                                <div className="dashboard-stat-row" style={{ marginTop: '16px', display: 'block' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span className="stat-label">Overall Task Completion</span>
                                        <span className="stat-value" style={{ fontSize: '0.9rem' }}>88%</span>
                                    </div>
                                    <div style={{ height: '8px', background: 'var(--lp-bg-off-white)', borderRadius: '4px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: '88%', background: 'var(--lp-primary-orange)', borderRadius: '4px' }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section id="how-it-works" className="how-it-works-section" style={{ position: 'relative', background: '#fff', padding: '120px 40px' }}>
                <div className="hiw-container hiw-split-grid" style={{ maxWidth: '1300px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '80px', alignItems: 'flex-start', paddingTop: '20px' }}>
                    {/* Left Column: Visual */}
                    <div className="hiw-visual scroll-animate animate-fade-in" style={{ background: 'var(--lp-secondary-navy)', borderRadius: '24px', padding: 0, position: 'relative', overflow: 'hidden', height: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 20px 50px -10px rgba(42, 63, 84, 0.3)' }}>
                        <div className="hiw-bg-pattern" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0.1, backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>

                        {/* Interface Mockup */}
                        <div className="interface-mockup" style={{ width: '100%', height: '100%', display: 'flex', padding: '40px 40px 0 40px', boxSizing: 'border-box' }}>
                            {/* Sidebar Mock */}
                            <div className="hiw-sidebar-mock" style={{ width: '80px', background: 'rgba(255,255,255,0.1)', borderTopLeftRadius: '12px', borderRight: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '30px', gap: '20px' }}>
                                <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: 'var(--lp-primary-orange)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', marginBottom: '10px' }}>
                                    <i className="fa-solid fa-layer-group"></i>
                                </div>
                                {/* Sidebar Icons */}
                                <div className="hiw-sidebar-icons" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.2rem', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    <i className="fa-solid fa-list-check" title="Tasks"></i>
                                    <i className="fa-solid fa-bell" title="Notices"></i>
                                    <i className="fa-solid fa-graduation-cap" title="Courses"></i>
                                    <i className="fa-solid fa-file-lines" title="Documents"></i>
                                    <i className="fa-solid fa-robot" title="AI"></i>
                                    <i className="fa-solid fa-chart-simple" title="Analytics"></i>
                                    <i className="fa-solid fa-shield-halved" title="Compliance"></i>
                                </div>
                            </div>

                            {/* Main Content Mock */}
                            <div className="hiw-main-mock" style={{ flex: 1, background: '#fff', borderTopRightRadius: '12px', padding: '30px', overflow: 'hidden', position: 'relative' }}>
                                {/* Header */}
                                <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '20px', marginBottom: '24px' }}>
                                    <div style={{ width: '150px', height: '20px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '12px' }}></div>
                                    <div style={{ display: 'flex', gap: '20px' }}>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--lp-secondary-navy)', borderBottom: '2px solid var(--lp-primary-orange)', paddingBottom: '4px' }}>Lifecycle</span>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#9ca3af' }}>Teams</span>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#9ca3af' }}>Roles & Permissions</span>
                                    </div>
                                </div>

                                {/* Lifecycle Visual */}
                                <div className="hiw-lifecycle-mock" style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                    {/* Stage 1 */}
                                    <div style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                            <div style={{ width: '8px', height: '8px', background: '#3b82f6', borderRadius: '50%' }}></div>
                                            <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--lp-secondary-navy)' }}>Preboarding</div>
                                        </div>
                                        <div style={{ height: '6px', width: '100%', background: '#f3f4f6', borderRadius: '3px', marginBottom: '8px' }}>
                                            <div style={{ width: '60%', height: '100%', background: '#3b82f6', borderRadius: '3px' }}></div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '-4px' }}>
                                            <div style={{ width: '20px', height: '20px', background: '#e5e7eb', borderRadius: '50%', border: '2px solid white' }}></div>
                                            <div style={{ width: '20px', height: '20px', background: '#d1d5db', borderRadius: '50%', border: '2px solid white', marginLeft: '-6px' }}></div>
                                        </div>
                                    </div>
                                    <i className="fa-solid fa-arrow-right hiw-lifecycle-arrows" style={{ color: '#d1d5db' }}></i>
                                    {/* Stage 2 */}
                                    <div style={{ flex: 1, border: '1px solid var(--lp-primary-orange)', background: 'rgba(232, 111, 74, 0.05)', borderRadius: '8px', padding: '16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                            <div style={{ width: '8px', height: '8px', background: 'var(--lp-primary-orange)', borderRadius: '50%' }}></div>
                                            <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--lp-secondary-navy)' }}>Onboarding</div>
                                        </div>
                                        <div style={{ height: '6px', width: '100%', background: 'rgba(232, 111, 74, 0.1)', borderRadius: '3px', marginBottom: '8px' }}>
                                            <div style={{ width: '40%', height: '100%', background: 'var(--lp-primary-orange)', borderRadius: '3px' }}></div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '-4px' }}>
                                            <div style={{ width: '20px', height: '20px', background: 'var(--lp-secondary-navy)', borderRadius: '50%', border: '2px solid white' }}></div>
                                            <div style={{ width: '20px', height: '20px', background: 'var(--lp-primary-orange)', borderRadius: '50%', border: '2px solid white', marginLeft: '-6px' }}></div>
                                            <div style={{ width: '20px', height: '20px', background: '#e5e7eb', borderRadius: '50%', border: '2px solid white', marginLeft: '-6px' }}></div>
                                        </div>
                                    </div>
                                    <i className="fa-solid fa-arrow-right hiw-lifecycle-arrows" style={{ color: '#d1d5db' }}></i>
                                    {/* Stage 3 */}
                                    <div style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', opacity: 0.6 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                            <div style={{ width: '8px', height: '8px', background: '#10b981', borderRadius: '50%' }}></div>
                                            <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--lp-secondary-navy)' }}>Active</div>
                                        </div>
                                        <div style={{ height: '6px', width: '100%', background: '#f3f4f6', borderRadius: '3px', marginBottom: '8px' }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Content */}
                    <div className="hiw-content scroll-animate animate-slide-up delay-100">
                        <h2 className="section-title" style={{ textAlign: 'left', marginBottom: '24px', fontSize: '2.5rem' }}>
                            Effortless Implementation,<br /><span style={{ color: 'var(--lp-primary-orange)' }}>Enterprise Capabilities</span>
                        </h2>
                        <p className="section-subtitle" style={{ textAlign: 'left', margin: '0 0 20px', fontSize: '1.15rem', color: 'var(--lp-text-muted)', lineHeight: 1.6 }}>
                            envion integrates seamlessly alongside your existing HRIS and workforce tools, enabling rapid implementation without disruption.
                        </p>
                        <p className="section-subtitle" style={{ textAlign: 'left', margin: '0 0 40px', fontSize: '1.15rem', color: 'var(--lp-text-muted)', lineHeight: 1.6 }}>
                            One premium, unified platform designed for organisations looking to maximise the return on every new hire and build high-performing teams from day one.
                        </p>

                        <div className="hiw-steps" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            {/* Step 1 */}
                            <div className="hiw-step-item" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                <div className="step-num" style={{ minWidth: '40px', height: '40px', background: 'rgba(232, 111, 74, 0.1)', color: 'var(--lp-primary-orange)', fontWeight: 700, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                                    1
                                </div>
                                <div>
                                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--lp-secondary-navy)', margin: 0 }}>Company Profile & Personalization</h4>
                                </div>
                            </div>

                            {/* Step 2 */}
                            <div className="hiw-step-item" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                <div className="step-num" style={{ minWidth: '40px', height: '40px', background: 'rgba(232, 111, 74, 0.1)', color: 'var(--lp-primary-orange)', fontWeight: 700, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                                    2
                                </div>
                                <div>
                                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--lp-secondary-navy)', margin: 0 }}>Roles & Permissions</h4>
                                </div>
                            </div>

                            {/* Step 3 */}
                            <div className="hiw-step-item" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                <div className="step-num" style={{ minWidth: '40px', height: '40px', background: 'rgba(232, 111, 74, 0.1)', color: 'var(--lp-primary-orange)', fontWeight: 700, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                                    3
                                </div>
                                <div>
                                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--lp-secondary-navy)', margin: 0 }}>Courses & Document Library</h4>
                                </div>
                            </div>

                            {/* Step 4 */}
                            <div className="hiw-step-item" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                <div className="step-num" style={{ minWidth: '40px', height: '40px', background: 'rgba(232, 111, 74, 0.1)', color: 'var(--lp-primary-orange)', fontWeight: 700, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                                    4
                                </div>
                                <div>
                                    <h4 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--lp-secondary-navy)', margin: 0 }}>Add Employees & Activate</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="cta-section" style={{ background: '#0f172a', padding: '60px 20px 100px', textAlign: 'center', color: 'white' }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                    <h2 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '20px', color: 'white', whiteSpace: 'nowrap' }}>
                        Ready to accelerate your onboarding success?
                    </h2>
                    <p style={{ fontSize: '1.25rem', marginBottom: '40px', color: '#cbd5e1', lineHeight: 1.6 }}>
                        Partner with us to build a bespoke onboarding solution tailored to your organisation's needs.
                    </p>
                    <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '20px' }}>
                        <a href="https://outlook.office.com/book/DiscoveryCall@envion.org.uk/?ismsaljsauthenabled"
                            className="btn-lp btn-lp-primary" style={{ padding: '16px 32px', fontSize: '1.1rem' }} target="_blank" rel="noopener noreferrer">
                            Book a Meeting
                        </a>
                        <Link to="/contact" className="btn-lp btn-lp-primary" style={{ background: 'transparent', border: '1px solid white', color: 'white', padding: '16px 32px', fontSize: '1.1rem' }}>
                            Get a Quote
                        </Link>
                    </div>
                    <p style={{ fontSize: '0.9rem', color: '#94a3b8' }}>No commitment required. Free consultation included.</p>
                </div>
            </section>

            {/* Footer */}
            <footer className="site-footer">
                <div className="footer-content">
                    <div className="footer-brand">
                        <h2><i className="fa-solid fa-layer-group"></i> envion</h2>
                        <p className="footer-subtitle" style={{ fontSize: '1.1rem', color: 'white', lineHeight: 1.5, marginBottom: '24px' }}>
                            envion is a modern workforce readiness platform. Enabling faster, smarter onboarding and preboarding for today’s organisations.
                        </p>
                        <div className="footer-contact-info">
                            <p><i className="fa-solid fa-envelope"></i> <a href="mailto:enquiries@envion.org.uk" style={{ color: 'white', textDecoration: 'none' }}>enquiries@envion.org.uk</a></p>
                        </div>
                    </div>

                    <div className="footer-column">
                        <h3>Solutions</h3>
                        <ul className="footer-links">
                            <li><Link to="/solutions#use-cases">Onboarding</Link></li>
                            <li><Link to="/solutions#use-cases">Preboarding</Link></li>
                        </ul>
                    </div>

                    <div className="footer-column">
                        <h3>Company</h3>
                        <ul className="footer-links">
                            <li><Link to="/about">About Us</Link></li>
                            <li><Link to="/contact">Contact</Link></li>
                        </ul>
                    </div>

                    <div className="footer-column">
                        <h3>Connect</h3>
                        <ul className="footer-social-links" style={{ listStyle: 'none', padding: 0, display: 'flex', gap: '16px', marginBottom: '24px' }}>
                            <li><a href="#" style={{ fontSize: '1.5rem', color: 'white' }}><i className="fa-brands fa-instagram"></i></a></li>
                            <li><a href="#" style={{ fontSize: '1.5rem', color: 'white' }}><i className="fa-brands fa-linkedin"></i></a></li>
                        </ul>
                        <div className="footer-compliance-badges" style={{ color: 'white', fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.4 }}>
                            <p style={{ marginBottom: '4px' }}>GDPR Compliant</p>
                            <p>Registered with ICO</p>
                        </div>
                    </div>
                </div>

                <div className="footer-bottom">
                    <div className="footer-bottom-grid">
                        <div className="footer-legal-info">
                            <p className="copyright">&copy; 2026 envion Inc. All rights reserved.</p>
                        </div>
                        <div className="footer-legal-links">
                            <Link to="/terms">Terms & Conditions</Link>
                            <Link to="/privacy">Privacy Policy</Link>
                            <Link to="/cookies">Cookies Policy</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
